import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Globe2, PlaneTakeoff, Radar, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { Panel, Btn, Overline } from "./ui";
import { ThreatMap, verdictColors } from "./ThreatMap";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";

const POLL_MS = 6000;

export function ThreatFeed({ onIntercept }) {
  const { t } = useI18n();
  const { viz } = useTheme();
  const THREAT_COLORS = verdictColors(viz);
  const [feed, setFeed] = useState(null);
  const [busy, setBusy] = useState(false);
  const lastImpossible = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/threat/feed?limit=60");
      setFeed(data);
      const li = data.summary.last_impossible;
      if (li && lastImpossible.current && li.event_id !== lastImpossible.current) {
        toast.error(t("tm.alert", { a: li.prev_city, b: li.city, s: li.speed_kmh }));
        onIntercept?.();
      }
      if (li) lastImpossible.current = li.event_id;
    } catch (err) {
      console.error("ThreatFeed poll failed:", err);
    }
  }, [t, onIntercept]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/threat/simulate?impossible=true");
      toast.error(t("tm.alert", { a: data.prev_city, b: data.city, s: data.speed_kmh }));
      lastImpossible.current = data.event_id;
      await load();
      onIntercept?.();
    } catch {
      toast.error("Sentinel injection failed");
    } finally {
      setBusy(false);
    }
  };

  const s = feed?.summary;
  const li = s?.last_impossible;

  return (
    <Panel
      icon={Globe2}
      title={t("tm.title")}
      subtitle={t("tm.sub")}
      accent="red"
      testid="threat-map-panel"
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5 font-mono text-[10px] text-aegis-emerald">
            <span className="h-1.5 w-1.5 rounded-full bg-aegis-emerald animate-pulseGlow" /> {t("tm.live")}
          </span>
          <Btn variant="redGhost" onClick={simulate} disabled={busy} testid="threat-simulate-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <PlaneTakeoff size={15} />} {t("tm.inject")}
          </Btn>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="xl:col-span-8">
          {feed ? (
            <ThreatMap events={feed.events} />
          ) : (
            <div className="flex h-[300px] items-center justify-center gap-3 rounded-lg border border-edge well text-sm text-slate-400">
              <Loader2 size={18} className="animate-spin text-aegis-cyan" /> {t("tm.loading")}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {["ALLOWED", "STEP_UP", "BLOCKED", "IMPOSSIBLE_TRAVEL"].map((k) => (
              <div key={k} className="rounded-lg border border-edge well px-3 py-2" data-testid={`tm-count-${k}`}>
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: THREAT_COLORS[k] }} />
                  <span className="font-mono text-[9px] uppercase tracking-overline text-slate-400">{t(`tm.v.${k}`)}</span>
                </div>
                <p className="mt-1 font-heading text-2xl font-light tabular" style={{ color: THREAT_COLORS[k] }}>
                  {s?.counts?.[k] ?? 0}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="xl:col-span-4">
          <div className="mb-3 rounded-lg border border-aegis-red/30 bg-aegis-red/[0.06] p-4" data-testid="impossible-travel-detector">
            <div className="flex items-center gap-2">
              <Radar size={15} className="text-aegis-red" />
              <Overline>{t("tm.detector")}</Overline>
            </div>
            {li ? (
              <>
                <p className="mt-2 font-mono text-[12px] text-aegis-red">
                  {li.prev_city} → {li.city}
                </p>
                <p className="mt-1 text-[12px] text-slate-300">{li.identity}</p>
                <p className="mt-1 font-mono text-[11px] text-slate-400">
                  {li.distance_km} km / {li.delta_minutes} min ≈ <span className="text-aegis-amber">{li.speed_kmh} km/h</span>
                </p>
                <p className="mt-1 font-mono text-[10px] text-slate-400">
                  {t("tm.threshold", { n: s.speed_threshold_kmh })}
                  {li.worm_block ? ` · WORM #${li.worm_block}` : ""}
                  {li.pending_action ? ` · ${li.pending_action}` : ""}
                </p>
              </>
            ) : (
              <p className="mt-2 text-[12px] text-slate-400">{t("tm.noAlert")}</p>
            )}
          </div>

          <div className="flex items-center justify-between px-1 pb-2">
            <Overline>{t("tm.feed")}</Overline>
            <span className="font-mono text-[10px] text-slate-400">
              {s ? t("tm.regions", { n: s.regions, i: s.identities }) : "—"}
            </span>
          </div>
          <div className="h-[320px] space-y-2 overflow-y-auto pr-1 no-scrollbar" data-testid="threat-feed-list">
            {(feed?.events || []).slice(0, 30).map((e) => (
              <motion.div
                key={e.event_id}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="rounded-lg border border-edge raise px-3 py-2"
                data-testid={`threat-event-${e.event_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 font-mono text-[10px]" style={{ color: THREAT_COLORS[e.verdict] }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: THREAT_COLORS[e.verdict] }} />
                    {t(`tm.v.${e.verdict}`)}
                  </span>
                  <span className="font-mono text-[10px] text-slate-400">{e.detected_at.slice(11, 19)}</span>
                </div>
                <p className="mt-1 truncate text-[12px] text-slate-200">
                  {e.city}, {e.country} · <span className="text-slate-400">{e.identity}</span>
                </p>
                <p className="truncate font-mono text-[10px] text-slate-400">
                  {e.ip} · {e.asn} · MFA {e.mfa}
                </p>
                {e.verdict === "IMPOSSIBLE_TRAVEL" && (
                  <p className="mt-1 flex items-center gap-1 font-mono text-[10px] text-aegis-red">
                    <ShieldAlert size={11} /> {e.prev_city} → {e.city} · {e.speed_kmh} km/h
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
}
